import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, Building2, Wifi, Phone, MapPin, Trash } from 'lucide-react';
import { toast } from 'sonner';
import { FormField } from './FormField';

const EMPTY_FORM = {
  name: '', slug: '', name_en: '', description_it: '', description_en: '',
  address: '', city: '', piva: '', phone: '', email: '',
  checkin_time: '15:00', checkout_time: '11:00', min_stay: 1,
  max_guests: 10, currency: 'EUR', tax_rate: 0,
  house_rules: '', house_rules_en: '',
  wifi_name: '', wifi_password: '',
  welcome_message_it: '', welcome_message_en: '',
  emergency_contacts: [], local_guide: [],
  is_active: true,
};

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadBrochure, setUploadBrochure] = useState(null);
  // For inline emergency contacts / local guide editors
  const [newContact, setNewContact] = useState({ name: '', phone: '' });
  const [newGuide, setNewGuide] = useState({ name: '', category: '', address: '', phone: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/properties');
      setProperties(res.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setPanelOpen(true); };
  const openEdit = (prop) => { setEditing(prop.id); setForm({ ...EMPTY_FORM, ...prop }); setPanelOpen(true); };

  const save = async () => {
    if (!form.name || !form.slug) { toast.error('Nome e slug obbligatori'); return; }
    setSaving(true);
    try {
      let res;
      if (editing) {
        res = await api.put(`/properties/${editing}`, form);
        toast.success('Proprietà aggiornata');
      } else {
        res = await api.post('/properties', form);
        toast.success('Proprietà creata');
      }
      const propId = res.data.id;
      if (uploadFile && propId) {
        const fd = new FormData();
        fd.append('file', uploadFile);
        await api.post(`/properties/${propId}/upload-logo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      if (uploadBrochure && propId) {
        const fd2 = new FormData();
        fd2.append('file', uploadBrochure);
        await api.post(`/properties/${propId}/upload-brochure`, fd2, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setPanelOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Eliminare questa proprietà?')) return;
    try {
      await api.delete(`/properties/${id}`);
      setProperties(prev => prev.filter(p => p.id !== id));
      toast.success('Proprietà eliminata');
    } catch { toast.error('Errore eliminazione'); }
  };

  const ff = (label, name, type = 'text', placeholder = '', rows) => (
    <FormField
      label={label} name={name} type={type}
      placeholder={placeholder} rows={rows}
      form={form} setForm={setForm}
      testId={`prop-field-${name}`}
    />
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Proprietà</h2>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-property-btn">
          <Plus size={14} /> Nuova Proprietà
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {properties.length === 0 ? (
            <div className="col-span-full card-dark rounded-lg p-12 text-center">
              <Building2 size={32} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
              <p className="text-[var(--lux-text-muted-3)] text-sm">Nessuna proprietà. Aggiungine una.</p>
            </div>
          ) : properties.map((prop) => (
            <div key={prop.id} className="card-dark rounded-lg p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-white font-medium">{prop.name}</h3>
                  <p className="text-[var(--lux-text-muted-3)] text-xs mt-0.5">{prop.city || '—'}</p>
                </div>
                <span className={`w-2 h-2 rounded-full mt-1.5 ${prop.is_active ? 'bg-green-500' : 'bg-[var(--lux-border-3)]'}`} />
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-[var(--lux-text-muted-2)] mb-4">
                <span>CI: {prop.checkin_time}</span>
                <span>CO: {prop.checkout_time}</span>
                <span>Min: {prop.min_stay}n</span>
                <span>Max: {prop.max_guests}p</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(prop)} className="btn-outline-gold text-xs flex items-center gap-1 flex-1 justify-center py-2"
                  data-testid={`edit-property-${prop.id}`}>
                  <Edit size={12} /> Modifica
                </button>
                <button onClick={() => remove(prop.id)} className="p-2 text-[var(--lux-text-muted-3)] hover:text-red-400 border border-[#222] hover:border-red-800 transition-colors"
                  data-testid={`delete-property-${prop.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">
              {editing ? 'Modifica Proprietà' : 'Nuova Proprietà'}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {ff('Nome *', 'name')}
              {ff('Slug URL *', 'slug')}
            </div>
            {ff('Nome (EN)', 'name_en')}
            {ff('Descrizione IT', 'description_it', 'text', '', 3)}
            {ff('Descrizione EN', 'description_en', 'text', '', 3)}
            <div className="grid grid-cols-2 gap-3">
              {ff('Città', 'city')}
              {ff('P.IVA', 'piva')}
            </div>
            {ff('Indirizzo', 'address')}
            <div className="grid grid-cols-2 gap-3">
              {ff('Telefono', 'phone')}
              {ff('Email', 'email', 'email')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {ff('Check-in', 'checkin_time')}
              {ff('Check-out', 'checkout_time')}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {ff('Soggiorno min.', 'min_stay', 'number')}
              {ff('Max ospiti', 'max_guests', 'number')}
              {ff('Tassa soggiorno %', 'tax_rate', 'number')}
            </div>
            {ff('Regole casa (IT)', 'house_rules', 'text', '', 4)}
            {ff('Regole casa (EN)', 'house_rules_en', 'text', '', 4)}

            {/* ---- Guest App Info ---- */}
            <div className="border-t border-[#222] pt-4 space-y-4">
              <p className="text-[#c9a84c] text-xs uppercase tracking-widest">Info App Ospite</p>
              {ff('Messaggio di benvenuto (IT)', 'welcome_message_it', 'text', 'Benvenuto nella nostra struttura...', 3)}
              {ff('Messaggio di benvenuto (EN)', 'welcome_message_en', 'text', 'Welcome to our property...', 3)}
              <div className="grid grid-cols-2 gap-3">
                {ff('Nome rete WiFi', 'wifi_name', 'text', 'ShangriLux_Guest')}
                {ff('Password WiFi', 'wifi_password', 'text', '••••••••')}
              </div>

              {/* Emergency contacts */}
              <div>
                <p className="label-muted text-xs mb-2 flex items-center gap-1"><Phone size={11} /> Contatti Emergenza</p>
                {(form.emergency_contacts || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <span className="text-white text-xs flex-1">{c.name} — {c.phone}</span>
                    <button type="button" onClick={() => setForm(p => ({
                      ...p, emergency_contacts: p.emergency_contacts.filter((_, j) => j !== i)
                    }))} className="p-1 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash size={11} /></button>
                  </div>
                ))}
                <div className="flex gap-2 mt-1">
                  <input className="input-dark text-xs flex-1" placeholder="Nome (es. Reception)" value={newContact.name}
                    onChange={e => setNewContact(p => ({ ...p, name: e.target.value }))} />
                  <input className="input-dark text-xs w-36" placeholder="Telefono" value={newContact.phone}
                    onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))} />
                  <button type="button" className="btn-outline-gold text-xs px-3 py-1.5" onClick={() => {
                    if (!newContact.name || !newContact.phone) return;
                    setForm(p => ({ ...p, emergency_contacts: [...(p.emergency_contacts || []), { ...newContact }] }));
                    setNewContact({ name: '', phone: '' });
                  }}>+</button>
                </div>
              </div>

              {/* Local guide */}
              <div>
                <p className="label-muted text-xs mb-2 flex items-center gap-1"><MapPin size={11} /> Guida Locale</p>
                {(form.local_guide || []).map((g, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <span className="text-white text-xs flex-1">{g.category && `[${g.category}] `}{g.name}</span>
                    <button type="button" onClick={() => setForm(p => ({
                      ...p, local_guide: p.local_guide.filter((_, j) => j !== i)
                    }))} className="p-1 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash size={11} /></button>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <input className="input-dark text-xs" placeholder="Nome (es. Ristorante Bello)" value={newGuide.name}
                    onChange={e => setNewGuide(p => ({ ...p, name: e.target.value }))} />
                  <input className="input-dark text-xs" placeholder="Categoria (es. Ristoranti)" value={newGuide.category}
                    onChange={e => setNewGuide(p => ({ ...p, category: e.target.value }))} />
                  <input className="input-dark text-xs" placeholder="Indirizzo" value={newGuide.address}
                    onChange={e => setNewGuide(p => ({ ...p, address: e.target.value }))} />
                  <input className="input-dark text-xs" placeholder="Telefono" value={newGuide.phone}
                    onChange={e => setNewGuide(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <button type="button" className="btn-outline-gold text-xs px-3 py-1.5 mt-2 w-full" onClick={() => {
                  if (!newGuide.name) return;
                  setForm(p => ({ ...p, local_guide: [...(p.local_guide || []), { ...newGuide }] }));
                  setNewGuide({ name: '', category: '', address: '', phone: '' });
                }}>+ Aggiungi luogo</button>
              </div>
            </div>

            {/* ---- Uploads ---- */}
            <div className="border-t border-[#222] pt-4 space-y-3">
              <p className="text-[#c9a84c] text-xs uppercase tracking-widest">Upload</p>
              <div>
                <label className="label-muted block mb-1 text-xs">Logo (immagine)</label>
                <input type="file" accept="image/*" onChange={e => setUploadFile(e.target.files[0])}
                  className="text-[var(--lux-text-sec)] text-sm" data-testid="prop-logo-upload" />
              </div>
              <div>
                <label className="label-muted block mb-1 text-xs">Brochure (PDF)</label>
                <input type="file" accept=".pdf,application/pdf" onChange={e => setUploadBrochure(e.target.files[0])}
                  className="text-[var(--lux-text-sec)] text-sm" data-testid="prop-brochure-upload" />
                {form.brochure_path && (
                  <p className="text-green-400 text-xs mt-1">Brochure caricata: {form.brochure_path.split('/').pop()}</p>
                )}
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="accent-[#c9a84c]" />
              <span className="text-sm text-[var(--lux-text-sec)]">Proprietà attiva</span>
            </label>
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1 flex items-center justify-center gap-2" data-testid="save-property-btn">
                {saving && <Loader2 size={13} className="animate-spin" />}
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
